import { createTRPCReact } from '@trpc/react-query';
import { getUntypedClient, httpBatchLink, httpLink, splitLink } from '@trpc/client';
import { QueryClient } from '@tanstack/react-query';
import { isAdminPath } from './adminPaths';
import { resolveApiBaseUrl } from './api-base-url';
import type { AppRouter } from '@shared/types/trpc/server/src/app-router';

type PersistedAdminAuth = {
  state?: {
    token?: string | null;
    admin?: unknown;
    isAuthenticated?: boolean;
  };
  token?: string | null;
};

const devConsole = typeof globalThis !== 'undefined' && globalThis.console
  ? globalThis.console
  : null;

const devLog = (...args: unknown[]) => {
  if (import.meta.env.DEV && devConsole) {
    devConsole.log(...args);
  }
};

const devWarn = (...args: unknown[]) => {
  if (import.meta.env.DEV && devConsole) {
    devConsole.warn(...args);
  }
};

const devError = (...args: unknown[]) => {
  if (import.meta.env.DEV && devConsole) {
    devConsole.error(...args);
  }
};

const safeGetLocalStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const resolveAdminToken = (): string | null => {
  // Prefer Zustand-persisted admin store
  const adminAuthStorage = safeGetLocalStorageItem('admin-auth-storage');
  if (adminAuthStorage) {
    try {
      // Zustand persist stores data in { state: { ... }, version: 0 } format
      const parsed = JSON.parse(adminAuthStorage) as PersistedAdminAuth;

      // Debug logging in development
      devLog('[resolveAdminToken] Parsed admin-auth-storage:', parsed);

      // Check both nested state and direct state for backward compatibility
      const token = parsed?.state?.token ?? parsed?.token ?? null;
      if (token) {
        devLog('[resolveAdminToken] Found token in admin-auth-storage');
        return token;
      }
    } catch (error) {
      devError('[resolveAdminToken] Failed to parse admin-auth-storage:', error);
      // ignore parse errors and fall through to other keys
    }
  }

  // Newer admin auth flow
  const adminAccessToken = safeGetLocalStorageItem('admin_access_token');
  if (adminAccessToken) {
    devLog('[resolveAdminToken] Found admin_access_token');
    return adminAccessToken;
  }

  // Legacy key kept for backward compatibility
  const legacyAdminToken = safeGetLocalStorageItem('adminToken');
  if (legacyAdminToken) {
    devLog('[resolveAdminToken] Found legacyAdminToken');
    return legacyAdminToken;
  }

  devWarn('[resolveAdminToken] No admin token found');
  return null;
};

const resolveAgencyToken = (): string | null => {
  return safeGetLocalStorageItem('agencyToken');
};

const resolvePartnerToken = (): string | null => {
  return safeGetLocalStorageItem('partnerToken');
};

const resolveAuthToken = (): string | null => {
  const userToken = safeGetLocalStorageItem('accessToken');
  const agencyToken = resolveAgencyToken();
  const partnerToken = resolvePartnerToken();
  const isBrowser = typeof window !== 'undefined';
  const pathname = isBrowser && typeof window.location?.pathname === 'string'
    ? window.location.pathname
    : '';

  const isAdminRoute = isAdminPath(pathname);
  const isAgencyRoute = pathname.startsWith('/agency');
  const isPartnerRoute = pathname.startsWith('/partner');
  const isMaintenanceRoute = pathname.startsWith('/maintenance');

  if (isPartnerRoute) {
    return partnerToken;
  }

  // Agency portal uses its own session token key
  // Also use agency token for maintenance page if available
  if (isAgencyRoute || (isMaintenanceRoute && agencyToken)) {
    if (agencyToken) {
      return agencyToken;
    }
  }

  if (isAdminRoute) {
    const adminToken = resolveAdminToken();
    return adminToken ?? userToken;
  }

  // デフォルトはユーザートークン、なければ代理店トークン
  return userToken || agencyToken || partnerToken;
};

export const trpc = createTRPCReact<AppRouter>();

const buildTrpcHeaders = () => {
  const token = resolveAuthToken();

  if (token) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  return {};
};

export const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition(op) {
        return op.type === 'mutation' && op.path === 'file.uploadViaServer';
      },
      true: httpLink({
        url: `${resolveApiBaseUrl()}/trpc`,
        // methodOverride を指定すると tRPC がクエリでも必ず POST を使う
        methodOverride: 'POST',
        headers: buildTrpcHeaders,
      }),
      false: httpBatchLink({
        url: `${resolveApiBaseUrl()}/trpc`,
        // methodOverride を指定すると tRPC がクエリでも必ず POST を使う
        methodOverride: 'POST',
        headers: buildTrpcHeaders,
      }),
    }),
  ],
  // トランスフォーマーを一時的に無効化
  // transformer: superjson,
});

export const trpcUntypedClient = getUntypedClient(trpcClient);

/**
 * TanStack Query (React Query) のデフォルト設定
 *
 * Phase 1 パフォーマンス最適化:
 * - staleTime: 5分 - データが新鮮と見なされる期間
 * - gcTime: 10分 - 未使用データがガベージコレクションされるまでの期間
 * - retry: 1回 - ネットワークエラー時の再試行回数
 * - refetchOnWindowFocus: false - ウィンドウフォーカス時の自動再取得を無効化
 * - refetchOnMount: true - コンポーネントマウント時にstaleなデータを再取得
 * - refetchOnReconnect: true - ネットワーク再接続時に再取得
 *
 * 想定効果:
 * - API呼び出し 10-15% 削減
 * - キャッシュヒット率 60-70%
 * - ページ遷移時の再レンダリング削減
 *
 * カスタマイズ例:
 * ```typescript
 * // リアルタイム性が必要なデータ
 * const { data } = trpc.alerts.getList.useQuery(undefined, {
 *   staleTime: 30 * 1000, // 30秒
 *   refetchInterval: 60 * 1000, // 1分ごとに自動更新
 * });
 *
 * // 静的なデータ
 * const { data } = trpc.companies.getById.useQuery(companyId, {
 *   staleTime: 30 * 60 * 1000, // 30分
 *   gcTime: 60 * 60 * 1000, // 1時間
 * });
 * ```
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // データが新鮮と見なされる期間（5分）
      // この期間内は再フェッチが発生しない
      staleTime: 5 * 60 * 1000,

      // 未使用データがガベージコレクションされるまでの期間（10分）
      // TanStack Query v5 で cacheTime から gcTime に名称変更
      gcTime: 10 * 60 * 1000,

      // エラー時の再試行回数
      // 1回のみ再試行（デフォルトは3回）
      retry: 1,

      // ウィンドウフォーカス時の自動再取得を無効化
      // 不要なAPI呼び出しを削減
      refetchOnWindowFocus: false,

      // コンポーネントマウント時にstaleなデータを再取得
      refetchOnMount: true,

      // ネットワーク再接続時に再取得
      refetchOnReconnect: true,
    },
    mutations: {
      // ミューテーション失敗時の再試行回数
      // 通常は再試行しない（ユーザーに明示的なエラー表示）
      retry: 0,
    },
  },
});



